import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { toastError } from "../../../helpers/Toast";

const printModeLabels = {
  DAILY: "Arkusz jednodniowy",
  WEEKLY: "Arkusz tygodniowy",
  POSITION: "Arkusz stanowiskowy"
};

const statusLabels = {
  DRAFT: "ROBOCZY",
  PUBLISHED: "OPUBLIKOWANY",
  ARCHIVED: "ARCHIWALNY"
};

const weekDayFormatter = new Intl.DateTimeFormat("pl-PL", {
  weekday: "long",
  day: "2-digit",
  month: "2-digit",
  year: "numeric"
});

const shortDayFormatter = new Intl.DateTimeFormat("pl-PL", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit"
});

const parseDate = value => {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

const dateToString = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const clampDate = (value, min, max) => {
  if (!value || value < min) return min;
  if (value > max) return max;
  return value;
};

const startOfWeek = value => {
  const date = parseDate(value);
  if (!date) return null;
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  return date;
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const buildDateRange = (from, to) => {
  const start = parseDate(from);
  const end = parseDate(to);
  if (!start || !end || end < start) return [];
  const result = [];
  for (let current = new Date(start); current <= end; current = addDays(current, 1)) {
    result.push(dateToString(current));
  }
  return result;
};

const formatTime = value => value ? String(value).slice(11, 16) : "";

const hoursBetween = (startsAt, endsAt) => {
  if (!startsAt || !endsAt) return 0;
  const diff = new Date(endsAt).getTime() - new Date(startsAt).getTime();
  return Math.max(0, diff / 3600000);
};

const formatHours = value => Number(value || 0).toLocaleString("pl-PL", {
  minimumFractionDigits: Number(value || 0) % 1 === 0 ? 0 : 2,
  maximumFractionDigits: 2
});

const escapeHtml = value => String(value ?? "")
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#039;");

const assignmentDate = assignment => String(assignment?.startsAt || "").slice(0, 10);
const employeeName = assignment => assignment?.employeeFullName || "BRAK OBSADY";
const positionName = assignment => assignment?.positionName || "Pozostałe stanowiska";
const locationName = assignment => assignment?.locationName || "—";
const positionKey = assignment => assignment?.positionId == null ? "NONE" : String(assignment.positionId);

const activeAssignments = schedule => (schedule?.assignments || [])
  .filter(item => item?.status !== "CANCELLED")
  .slice()
  .sort((a, b) => String(a.startsAt || "").localeCompare(String(b.startsAt || "")) || employeeName(a).localeCompare(employeeName(b), "pl"));

const buildSummaryHtml = assignments => {
  const totalHours = assignments.reduce((sum, item) => sum + hoursBetween(item.startsAt, item.endsAt), 0);
  const open = assignments.filter(item => item.status === "OPEN" || !item.employeeId).length;
  return `
    <div class="summary">
      <span><b>Zmiany:</b> ${assignments.length}</span>
      <span><b>Roboczogodziny:</b> ${formatHours(totalHours)} h</span>
      <span><b>Brak obsady:</b> ${open}</span>
    </div>
  `;
};

const tableHtml = (headers, rows, className = "") => `
  <table class="${className}">
    <thead><tr>${headers.map(header => `<th>${escapeHtml(header)}</th>`).join("")}</tr></thead>
    <tbody>${rows.length
      ? rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join("")}</tr>`).join("")
      : `<tr><td colspan="${headers.length}" class="empty">Brak zmian w wybranym zakresie.</td></tr>`}
    </tbody>
  </table>
`;

const dailyHtml = (assignments, date) => {
  const groups = new Map();
  assignments.forEach(item => {
    const key = positionName(item);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });

  const sections = [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "pl"))
    .map(([position, items]) => {
      const rows = items.map(item => [
        escapeHtml(employeeName(item)),
        escapeHtml(locationName(item)),
        escapeHtml(`${formatTime(item.startsAt)}–${formatTime(item.endsAt)}`),
        escapeHtml(`${formatHours(hoursBetween(item.startsAt, item.endsAt))} h`),
        item.status === "OPEN" || !item.employeeId ? '<b class="open">BRAK OBSADY</b>' : ""
      ]);
      return `
        <section class="position-section">
          <h2>${escapeHtml(position)}</h2>
          ${tableHtml(["Pracownik", "Lokalizacja", "Godziny", "Czas", "Uwagi"], rows)}
        </section>
      `;
    })
    .join("");

  return `
    <div class="period-title">${escapeHtml(weekDayFormatter.format(parseDate(date)))}</div>
    ${buildSummaryHtml(assignments)}
    ${sections || '<div class="empty-block">Brak zmian w wybranym dniu.</div>'}
  `;
};

const weeklyHtml = (assignments, from, to) => {
  const dates = buildDateRange(from, to);
  const rows = new Map();

  assignments.forEach((item, index) => {
    const key = item.employeeId ? `E:${item.employeeId}` : `OPEN:${item.id ?? index}`;
    if (!rows.has(key)) {
      rows.set(key, {
        name: employeeName(item),
        open: !item.employeeId || item.status === "OPEN",
        byDate: Object.fromEntries(dates.map(date => [date, []]))
      });
    }
    const date = assignmentDate(item);
    if (rows.get(key).byDate[date]) rows.get(key).byDate[date].push(item);
  });

  const orderedRows = [...rows.values()].sort((a, b) => {
    if (a.open !== b.open) return a.open ? 1 : -1;
    return a.name.localeCompare(b.name, "pl");
  });

  const bodyRows = orderedRows.map(row => [
    `<b>${escapeHtml(row.name)}</b>`,
    ...dates.map(date => {
      const shifts = row.byDate[date] || [];
      if (!shifts.length) return "";
      return shifts.map(item => `
        <div class="week-shift ${row.open ? "open-shift" : ""}">
          <b>${escapeHtml(`${formatTime(item.startsAt)}–${formatTime(item.endsAt)}`)}</b>
          <span>${escapeHtml(positionName(item))}</span>
          ${item.locationName ? `<span>${escapeHtml(item.locationName)}</span>` : ""}
        </div>
      `).join("");
    })
  ]);

  const headers = ["Pracownik", ...dates.map(date => shortDayFormatter.format(parseDate(date)))];

  return `
    <div class="period-title">${escapeHtml(from)} – ${escapeHtml(to)}</div>
    ${buildSummaryHtml(assignments)}
    ${tableHtml(headers, bodyRows, "weekly-table")}
  `;
};

const positionHtml = (assignments, from, to, selectedPositionName) => {
  const rows = assignments.map(item => [
    escapeHtml(weekDayFormatter.format(parseDate(assignmentDate(item)))),
    escapeHtml(employeeName(item)),
    escapeHtml(locationName(item)),
    escapeHtml(`${formatTime(item.startsAt)}–${formatTime(item.endsAt)}`),
    escapeHtml(`${formatHours(hoursBetween(item.startsAt, item.endsAt))} h`),
    item.status === "OPEN" || !item.employeeId ? '<b class="open">BRAK OBSADY</b>' : ""
  ]);

  return `
    <div class="period-title">${escapeHtml(selectedPositionName)} · ${escapeHtml(from)} – ${escapeHtml(to)}</div>
    ${buildSummaryHtml(assignments)}
    ${tableHtml(["Dzień", "Pracownik", "Lokalizacja", "Godziny", "Czas", "Uwagi"], rows)}
  `;
};

const buildPrintDocument = ({ schedule, mode, positionLabel, body }) => {
  const landscape = mode === "WEEKLY";
  const generatedAt = new Intl.DateTimeFormat("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date());

  return `<!doctype html>
<html lang="pl">
<head>
<meta charset="utf-8">
<title>${escapeHtml(`Grafik ${schedule.departmentName || ""}`)}</title>
<style>
  @page { size: A4 ${landscape ? "landscape" : "portrait"}; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; font-size: 10.5pt; }
  .header { border-bottom: 2px solid #222; padding-bottom: 7px; margin-bottom: 10px; display: flex; justify-content: space-between; gap: 20px; align-items: flex-end; }
  .header h1 { font-size: 18pt; margin: 0 0 3px; }
  .header .meta { line-height: 1.45; }
  .header .status { font-weight: 700; border: 1px solid #555; padding: 4px 7px; white-space: nowrap; }
  .period-title { font-size: 13pt; font-weight: 700; margin: 8px 0; text-transform: capitalize; }
  .summary { display: flex; gap: 22px; border: 1px solid #bbb; padding: 6px 8px; margin-bottom: 10px; }
  .position-section { break-inside: avoid; margin-bottom: 12px; }
  .position-section h2 { font-size: 12pt; margin: 8px 0 4px; border-bottom: 1px solid #999; padding-bottom: 2px; }
  .position-print-page { break-inside: avoid; }
  .position-print-page.page-break { break-before: page; page-break-before: always; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  th, td { border: 1px solid #888; padding: 4px 5px; vertical-align: top; overflow-wrap: anywhere; }
  th { background: #eee; text-align: left; font-weight: 700; }
  tbody tr:nth-child(even) td { background: #fafafa; }
  .weekly-table { font-size: 8.4pt; }
  .weekly-table th:first-child, .weekly-table td:first-child { width: 24mm; }
  .week-shift { display: flex; flex-direction: column; gap: 1px; padding-bottom: 3px; margin-bottom: 3px; border-bottom: 1px dotted #aaa; }
  .week-shift:last-child { border-bottom: 0; margin-bottom: 0; padding-bottom: 0; }
  .open, .open-shift { font-weight: 700; }
  .open-shift { border-left: 3px solid #222; padding-left: 4px; }
  .empty, .empty-block { text-align: center; padding: 18px; color: #555; }
  .footer { margin-top: 10px; padding-top: 5px; border-top: 1px solid #aaa; font-size: 8pt; color: #555; display: flex; justify-content: space-between; }
  thead { display: table-header-group; }
</style>
</head>
<body>
  <div class="header">
    <div>
      <h1>${escapeHtml(schedule.departmentName || "Grafik pracy")}</h1>
      <div class="meta">
        <div><b>Grafik:</b> ${escapeHtml(schedule.name || "")}</div>
        <div><b>Rodzaj wydruku:</b> ${escapeHtml(printModeLabels[mode])}${mode === "POSITION" ? ` · ${escapeHtml(positionLabel)}` : ""}</div>
      </div>
    </div>
    <div class="status">${escapeHtml(statusLabels[schedule.status] || schedule.status || "")}</div>
  </div>
  ${body}
  <div class="footer">
    <span>Qstaff · ${escapeHtml(schedule.departmentName || "")}</span>
    <span>Wygenerowano: ${escapeHtml(generatedAt)}</span>
  </div>
</body>
</html>`;
};

const SchedulePrintDialog = ({ show, onHide, schedule }) => {
  const [mode, setMode] = useState("DAILY");
  const [dailyDate, setDailyDate] = useState(schedule?.dateFrom || "");
  const [weekAnchor, setWeekAnchor] = useState(schedule?.dateFrom || "");
  const [positionDateFrom, setPositionDateFrom] = useState(schedule?.dateFrom || "");
  const [positionDateTo, setPositionDateTo] = useState(schedule?.dateTo || "");
  const [positionSelectionMode, setPositionSelectionMode] = useState("ALL");
  const [selectedPositionIds, setSelectedPositionIds] = useState([]);

  const positions = useMemo(() => {
    const result = new Map();
    activeAssignments(schedule).forEach(item => {
      const key = positionKey(item);
      if (!result.has(key)) result.set(key, positionName(item));
    });
    return [...result.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "pl"));
  }, [schedule]);

  useEffect(() => {
    if (!schedule) return;
    setDailyDate(schedule.dateFrom);
    setWeekAnchor(schedule.dateFrom);
    setPositionDateFrom(schedule.dateFrom);
    setPositionDateTo(schedule.dateTo);
    setPositionSelectionMode("ALL");
    setSelectedPositionIds([]);
    setMode("DAILY");
  }, [schedule?.id, schedule?.dateFrom, schedule?.dateTo]);

  const weeklyRange = useMemo(() => {
    if (!schedule) return { from: "", to: "" };
    const monday = startOfWeek(clampDate(weekAnchor, schedule.dateFrom, schedule.dateTo));
    if (!monday) return { from: schedule.dateFrom, to: schedule.dateFrom };
    const sunday = addDays(monday, 6);
    return {
      from: clampDate(dateToString(monday), schedule.dateFrom, schedule.dateTo),
      to: clampDate(dateToString(sunday), schedule.dateFrom, schedule.dateTo)
    };
  }, [schedule, weekAnchor]);

  const selectedPositionKeys = useMemo(
    () => positionSelectionMode === "ALL" ? positions.map(position => position.id) : selectedPositionIds,
    [positionSelectionMode, positions, selectedPositionIds]
  );

  const previewCount = useMemo(() => {
    if (!schedule) return 0;
    const assignments = activeAssignments(schedule);
    if (mode === "DAILY") return assignments.filter(item => assignmentDate(item) === dailyDate).length;
    if (mode === "WEEKLY") return assignments.filter(item => assignmentDate(item) >= weeklyRange.from && assignmentDate(item) <= weeklyRange.to).length;
    return assignments.filter(item => {
      const date = assignmentDate(item);
      return selectedPositionKeys.includes(positionKey(item)) && date >= positionDateFrom && date <= positionDateTo;
    }).length;
  }, [schedule, mode, dailyDate, weeklyRange, selectedPositionKeys, positionDateFrom, positionDateTo]);

  const togglePosition = id => {
    setSelectedPositionIds(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id]);
  };

  const print = () => {
    if (!schedule) return;
    let assignments = activeAssignments(schedule);
    let positionLabel = "";
    let body = "";

    if (mode === "DAILY") {
      if (!dailyDate) {
        toastError("Wybierz dzień do wydruku.");
        return;
      }
      assignments = assignments.filter(item => assignmentDate(item) === dailyDate);
      body = dailyHtml(assignments, dailyDate);
    } else if (mode === "WEEKLY") {
      assignments = assignments.filter(item => assignmentDate(item) >= weeklyRange.from && assignmentDate(item) <= weeklyRange.to);
      body = weeklyHtml(assignments, weeklyRange.from, weeklyRange.to);
    } else {
      if (!positionDateFrom || !positionDateTo || positionDateTo < positionDateFrom) {
        toastError("Podaj prawidłowy zakres dat wydruku stanowiskowego.");
        return;
      }
      const dateFrom = clampDate(positionDateFrom, schedule.dateFrom, schedule.dateTo);
      const dateTo = clampDate(positionDateTo, schedule.dateFrom, schedule.dateTo);
      const keys = selectedPositionKeys;
      if (keys.length === 0) {
        toastError("Wybierz co najmniej jedno stanowisko do wydruku.");
        return;
      }
      const selectedPositions = positions.filter(position => keys.includes(position.id));
      positionLabel = positionSelectionMode === "ALL" ? "Wszystkie stanowiska" : `${selectedPositions.length} wybranych stanowisk`;
      body = selectedPositions.map((position, index) => {
        const positionAssignments = assignments.filter(item => {
          const date = assignmentDate(item);
          return positionKey(item) === position.id && date >= dateFrom && date <= dateTo;
        });
        return `<section class="position-print-page${index > 0 ? " page-break" : ""}">${positionHtml(positionAssignments, dateFrom, dateTo, position.name)}</section>`;
      }).join("");
    }

    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      toastError("Przeglądarka zablokowała okno wydruku. Zezwól na wyskakujące okna dla Qstaff.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(buildPrintDocument({ schedule, mode, positionLabel, body }));
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => printWindow.print(), 150);
  };

  if (!schedule) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Druk grafiku</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <b>{schedule.departmentName}</b> · {schedule.name}<br />
          <small>{schedule.dateFrom} – {schedule.dateTo}</small>
        </div>

        <div className="alert alert-info py-2">
          Wydruk dotyczy wyłącznie działu <b>{schedule.departmentName}</b>. Aby wydrukować inny dział, otwórz jego grafik osobno.
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Rodzaj wydruku</Form.Label>
          <Form.Select value={mode} onChange={event => setMode(event.target.value)}>
            {Object.entries(printModeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </Form.Select>
        </Form.Group>

        {mode === "DAILY" && (
          <Form.Group>
            <Form.Label>Dzień</Form.Label>
            <Form.Control
              type="date"
              min={schedule.dateFrom}
              max={schedule.dateTo}
              value={dailyDate}
              onChange={event => setDailyDate(clampDate(event.target.value, schedule.dateFrom, schedule.dateTo))}
            />
          </Form.Group>
        )}

        {mode === "WEEKLY" && (
          <>
            <Form.Group>
              <Form.Label>Dowolny dzień tygodnia</Form.Label>
              <Form.Control
                type="date"
                min={schedule.dateFrom}
                max={schedule.dateTo}
                value={weekAnchor}
                onChange={event => setWeekAnchor(clampDate(event.target.value, schedule.dateFrom, schedule.dateTo))}
              />
            </Form.Group>
            <div className="form-text mt-2">Wydruk: {weeklyRange.from} – {weeklyRange.to}. Zakres jest automatycznie ograniczany do dat grafiku.</div>
          </>
        )}

        {mode === "POSITION" && (
          <div className="row g-3">
            <div className="col-md-12">
              <Form.Label>Stanowiska do wydruku</Form.Label>
              <div className="border rounded p-3">
                <Form.Check
                  type="radio"
                  name="positionSelectionMode"
                  id="position-print-all"
                  label="Wszystkie stanowiska — każde od nowej strony"
                  checked={positionSelectionMode === "ALL"}
                  onChange={() => setPositionSelectionMode("ALL")}
                />
                <Form.Check
                  type="radio"
                  name="positionSelectionMode"
                  id="position-print-selected"
                  label="Tylko wybrane stanowiska"
                  checked={positionSelectionMode === "SELECTED"}
                  onChange={() => setPositionSelectionMode("SELECTED")}
                />

                {positionSelectionMode === "SELECTED" && (
                  <div className="mt-3">
                    <div className="d-flex gap-2 mb-2">
                      <Button type="button" size="sm" variant="outline-secondary" onClick={() => setSelectedPositionIds(positions.map(position => position.id))}>Zaznacz wszystkie</Button>
                      <Button type="button" size="sm" variant="outline-secondary" onClick={() => setSelectedPositionIds([])}>Wyczyść</Button>
                    </div>
                    <div className="border rounded p-2" style={{ maxHeight: "220px", overflowY: "auto" }}>
                      {positions.length === 0 && <div className="text-muted small">Brak stanowisk w grafiku.</div>}
                      {positions.map(position => (
                        <Form.Check
                          key={position.id}
                          type="checkbox"
                          id={`position-print-${position.id}`}
                          label={position.name}
                          checked={selectedPositionIds.includes(position.id)}
                          onChange={() => togglePosition(position.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Od</Form.Label>
                <Form.Control
                  type="date"
                  min={schedule.dateFrom}
                  max={schedule.dateTo}
                  value={positionDateFrom}
                  onChange={event => setPositionDateFrom(clampDate(event.target.value, schedule.dateFrom, schedule.dateTo))}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Do</Form.Label>
                <Form.Control
                  type="date"
                  min={schedule.dateFrom}
                  max={schedule.dateTo}
                  value={positionDateTo}
                  onChange={event => setPositionDateTo(clampDate(event.target.value, schedule.dateFrom, schedule.dateTo))}
                />
              </Form.Group>
            </div>
          </div>
        )}

        <div className="alert alert-light border mt-3 mb-0">
          Wydruk obejmie <b>{previewCount}</b> aktywnych zmian. Zmiany anulowane są pomijane, a nieobsadzone zmiany są drukowane jako <b>BRAK OBSADY</b>.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Anuluj</Button>
        <Button variant="primary" onClick={print}>Drukuj</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SchedulePrintDialog;
